import React, { useState } from 'react'
import { DownloadOutlined } from '@ant-design/icons'
import {
  Button,
  Modal,
  Select,
  Spin,
  Tabs,
} from '@surecompanies/core_components'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import Page from 'layouts/Page'
import { AncillaryReportTabKey } from './api/ancillary-report.types'
import { visibleExtraLines } from './utils/ancillary-report-cells'
import { AncillaryReportGrid } from './components/AncillaryReportGrid'
import {
  describeReportError,
  useAncillaryReport,
} from './hooks/useAncillaryReport'
import { useAncillaryNoyoSync } from './hooks/useAncillaryNoyoSync'
import { ancillaryReportPath } from './paths'

const { TabPane } = Tabs

/** @internal exported for unit tests */
export const ANCILLARY_REPORT_TABS: ReadonlyArray<{
  key: AncillaryReportTabKey
  title: string
}> = [
  { key: 'lookingGood', title: 'Everything matches' },
  { key: 'recentlySubmitted', title: 'Recently submitted' },
  { key: 'termedStillCovered', title: 'Lingering Benefit' },
  { key: 'notMatch', title: 'Inconsistent' },
]

const ASSIGNED_TAB: { key: AncillaryReportTabKey; title: string } = {
  key: 'assigned',
  title: 'Assigned',
}

const PACIFIC_TIME_ZONE = 'America/Los_Angeles'

function snapshotPart(
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes
): string {
  return parts.find((part) => part.type === type)?.value ?? ''
}

/** Pacific Time, e.g. "13 October, 2026, 3am Pacific Time". */
function formatSnapshotDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: PACIFIC_TIME_ZONE,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date)

  const day = snapshotPart(parts, 'day')
  const month = snapshotPart(parts, 'month')
  const year = snapshotPart(parts, 'year')
  const hour = snapshotPart(parts, 'hour')
  const minute = snapshotPart(parts, 'minute')
  const dayPeriod = snapshotPart(parts, 'dayPeriod').toLowerCase()

  if (
    day === '' ||
    month === '' ||
    year === '' ||
    hour === '' ||
    dayPeriod === ''
  ) {
    return value
  }

  const clock =
    minute === '' || minute === '00'
      ? `${hour}${dayPeriod}`
      : `${hour}:${minute}${dayPeriod}`

  return `${day} ${month}, ${year}, ${clock} Pacific Time`
}

export default function AncillaryReport() {
  const {
    clients,
    isClientsLoading,
    clientPublicKey,
    selectClient,
    report,
    isReportLoading,
    reportError,
    download,
    isDownloading,
    isPlatformAdmin,
    assigneeOptions,
    assign,
    isAssigning,
    triggerSnapshot,
    isTriggering,
  } = useAncillaryReport()
  const { syncEmployee, syncingEmployeePublicKeys } = useAncillaryNoyoSync()
  const [activeTab, setActiveTab] =
    useState<AncillaryReportTabKey>('lookingGood')

  const hasNoClients = !isClientsLoading && clients.length === 0
  // The app chrome already shows the company. Repeat it here only as the
  // platform-admin client switcher, never as a second title.
  const showClientDropdown = isPlatformAdmin && clients.length > 0

  return (
    <Page
      title="Ancillary Dashboard"
      breadcrumb={
        <>
          <BreadcrumbNav
            items={[
              { title: 'Ancillary Dashboard', path: ancillaryReportPath() },
            ]}
          />
          {showClientDropdown && (
            <Select
              showSearch
              optionFilterProp="label"
              value={clientPublicKey}
              onChange={(value: string) => selectClient(value)}
              options={clients.map((client) => ({
                value: client.id,
                label: client.name,
              }))}
              style={{ width: 320, marginTop: 16 }}
              aria-label="Select client"
            />
          )}
        </>
      }
      subTitle={
        report ? (
          <span>
            Guardian snapshot, as of {formatSnapshotDate(report.snapshotDate)}
          </span>
        ) : undefined
      }
      action={
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {isPlatformAdmin && (
            <Button
              onClick={() =>
                Modal.confirm({
                  title: 'Run snapshot?',
                  content:
                    'This runs the full daily snapshot for every snapshot-enabled client, not only the client selected on this page.',
                  okText: 'Run snapshot',
                  cancelText: 'Cancel',
                  onOk: () => triggerSnapshot(),
                })
              }
              loading={isTriggering}
            >
              Run snapshot
            </Button>
          )}
          <Button
            icon={<DownloadOutlined />}
            onClick={() => download()}
            loading={isDownloading}
            disabled={!report}
          >
            Download
          </Button>
        </div>
      }
      error={
        hasNoClients
          ? {
              type: 'info',
              message:
                'The ancillary dashboard is not enabled for your clients.',
            }
          : reportError
            ? { type: 'error', message: describeReportError(reportError) }
            : null
      }
    >
      {(isClientsLoading || isReportLoading) && <Spin />}
      {report && (
        <Tabs
          activeKey={activeTab}
          onChange={(key: string) => setActiveTab(key as AncillaryReportTabKey)}
        >
          {(isPlatformAdmin
            ? [...ANCILLARY_REPORT_TABS, ASSIGNED_TAB]
            : ANCILLARY_REPORT_TABS
          ).map((tab) => (
            <TabPane
              tab={`${tab.title} (${report[tab.key].length.toLocaleString('en-US')})`}
              key={tab.key}
            >
              <AncillaryReportGrid
                rows={report[tab.key]}
                extraLines={visibleExtraLines([
                  ...report.lookingGood,
                  ...report.recentlySubmitted,
                  ...report.termedStillCovered,
                  ...report.notMatch,
                  ...report.assigned,
                ])}
                isPlatformAdmin={isPlatformAdmin}
                assigneeOptions={assigneeOptions}
                isAssigning={isAssigning}
                onAssign={assign}
                onSync={syncEmployee}
                syncingEmployeePublicKeys={syncingEmployeePublicKeys}
              />
            </TabPane>
          ))}
        </Tabs>
      )}
    </Page>
  )
}
